import { ReactNode, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Tooltip, TooltipProps, Box } from '@mui/material'
import { Snackbar, IconButton, IconButtonProps } from '@carto/meridian-ds/components'
import { ContentCopyOutlined } from '@mui/icons-material'
import { useCopyValue } from './useCopyValue'

interface CopiableComponentProps {
  children?: ReactNode
  disabled?: boolean
  value: string
  onCopied?: () => void
  copyText?: string
  copiedText?: string
  button?: boolean
  buttonLabel?: string
  buttonProps?: Omit<IconButtonProps, 'icon'>
  ariaLabel?: string
  dataTestId?: string
  tooltipPlacement?: TooltipProps['placement']
}

const CopiableComponent = ({
  children,
  disabled,
  value,
  onCopied,
  copyText = 'common.button.copy',
  copiedText = 'common.button.copiedToClipboard',
  button,
  buttonLabel,
  buttonProps,
  ariaLabel,
  dataTestId,
  tooltipPlacement = 'top'
}: CopiableComponentProps) => {
  const [open, setOpen] = useState(false)
  const { copy } = useCopyValue(copyText, copiedText)

  const handleClick = () => {
    copy(value)
    onCopied?.()
    setOpen(true)
  }

  return (
    <>
      <Tooltip title={<FormattedMessage id={copyText} />} leaveDelay={0} placement={tooltipPlacement}>
        <Box sx={{ cursor: disabled ? undefined : 'pointer' }} onClick={handleClick}>
          {children}
          {button && (
            <IconButton
              {...buttonProps}
              disabled={disabled}
              icon={<ContentCopyOutlined />}
              data-testid={dataTestId}
              aria-label={ariaLabel || buttonLabel}
              sx={{
                display: 'flex'
              }}
            />
          )}
        </Box>
      </Tooltip>
      <Snackbar open={open} autoWidth closeable={false} autoHideDuration={4000} onClose={() => setOpen(false)}>
        <FormattedMessage id={copiedText} />
      </Snackbar>
    </>
  )
}

export default CopiableComponent
